import React, { useCallback, useMemo, useState } from 'react';
import { useUploadManager } from '@design-stack-vista/upload-components';
import { AssetStatusType } from '@design-stack-vista/vista-assets-sdk';
import { Button, Card, FlexBox, Spinner, Typography } from '@vp/swan';
import { AnalyticsEventData } from 'src/components/Analytics';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { abortControllerManager } from 'src/managers/AbortControllerManager';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { formatBytes } from 'src/utils/uploadUtil';
import { useFileUpload } from './useFileUpload';

interface UploadStatusProps {
  isMobile: boolean;
  isUploading: boolean;
  isValidating: boolean;
  fileSize: number;
}

export const UploadStatus = ({ isMobile, isValidating, isUploading, fileSize }: UploadStatusProps) => {
  const { assetStore } = useUploadManager();
  const { resetUploader } = useFileUpload();
  const [uploadedSize, setUploadedSize] = useState(formatBytes(0));
  const { sendSegmentTrackingEvent } = useTrackingEvent();

  // The first asset in the assetStore.assets array is the one that is currently being uploaded
  const asset = assetStore?.assets[0];

  const uploadProgress = useMemo(() => {
    if (asset?.status.type === AssetStatusType.Preparing) {
      const uploadedSizeStatus = (Number(asset?.status?.uploadProgress) * fileSize) / 100;
      setUploadedSize(formatBytes(uploadedSizeStatus));
      return asset?.status.uploadProgress !== undefined ? Number(asset.status.uploadProgress) : 0;
    }
    if (asset?.status.type === AssetStatusType.Prepared) {
      return 100;
    }
    return 0;
  }, [asset?.status, fileSize]);

  const onCancelClick = useCallback(() => {
    sendSegmentTrackingEvent({
      action: UPLOAD_UNIFIED_EVENT,
      category: 'Design - Upload and Print',
      uploadStep: UploadSteps.UploadCanceled,
      label: 'Cancel',
    } as AnalyticsEventData);
    abortControllerManager.abort();
    resetUploader();
  }, [resetUploader, sendSegmentTrackingEvent]);

  return (
    <Card bordered paddingY={isValidating ? 7 : 6} compactMode={isMobile} className="upload-status-card">
      <FlexBox justifyContent="center" gap="4" mb={isValidating ? 4 : 3}>
        <Spinner accessibleText={text('loading')} size="standard" />
        {isValidating && <Typography fontSkin="body-small-bold">{text('processing-upload')}</Typography>}
        {isUploading && (
          <Typography fontSkin="body-small-bold">
            {text('uploading-with-percentage', { percentage: uploadProgress })}
          </Typography>
        )}
      </FlexBox>
      {isUploading && (
        <Typography fontSize="xsmall" textColor="subtle" textAlign="center">
          {text('uploading-size-progress', {
            uploaded: uploadedSize,
            total: formatBytes(fileSize),
          })}
        </Typography>
      )}
      <Typography fontSize={'xsmall'} textColor="subtle" textAlign="center" mb={3}>
        {text('processing-secondary')}
      </Typography>
      {(isValidating || isUploading) && (
        <Button width="full-width" compactMode onClick={onCancelClick}>
          {text('cancel-upload-label')}
        </Button>
      )}
    </Card>
  );
};
